import { useEffect } from "react";

import { useForm } from "react-hook-form";

import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import { IShippingPolicy, ShippingPolicySource } from './../../models/ShippingPolicy';
import useShippingPolicies from "../../data/useShippingPolicies";
import { TextInput } from "../shared/Inputs/TextInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";

type FormValues = {
  name: string,
  price: string,
  daysToDeliver: number,
  handlingTime: number,
  policyId: string,
  shippingProviderId?: string,
  courier?: string,
  excludeNorthernIreland: boolean,
};

interface ShippingPolicyModalProps {
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  policy?: IShippingPolicy;
}

export default function ShippingPolicyModal({ open, setOpen, policy }: ShippingPolicyModalProps) {
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Shipping Policy");
  const { create, update, remove } = useShippingPolicies();
  const { register, handleSubmit, reset, watch, setValue } = useForm<FormValues>();

  const onSubmit = (data: FormValues) => {
    if (policy) {
      update.mutate({ ...policy, ...data, price: parseFloat(data.price) }, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      create.mutate({ ...data, price: parseFloat(data.price) }, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  };

  const handleDelete = (policyId: string) => {
    remove.mutate(policyId, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  };


  useEffect(() => {
    if (policy !== undefined) {
      reset({ ...policy, price: String(policy.price), courier: policy.courier || "", excludeNorthernIreland: !!policy.excludeNorthernIreland });
    } else {
      console.log("Resetting form to empty state");
      reset({ excludeNorthernIreland: false });
    }
  }, [policy, open, reset]);

  // eBay owns the exclusion list for its own policies - Wilma re-reads it on every sync
  const isEbayPolicy = policy?.source === ShippingPolicySource.Ebay;

  return (
    <Modal width="max-w-[40vw]" open={open} setOpen={setOpen} >
      <div className="flex justify-between">
        <h1 className="text-xl">{`${policy !== undefined ? "Edit" : "Add"} a Shipping Policy`}</h1>
      </div>
      <div className="mt-4">
        <form className="pt-4 border-t border-gray-200 grid grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="col-span-2">
            <TextInput label="Name" registerName="name" register={register} />
          </div>
          <NumberInput label="Price" registerName="price" register={register} />
          <NumberInput label="Days to Deliver" registerName="daysToDeliver" register={register} />
          <NumberInput label="Handling Time (Hours)" registerName="handlingTime" register={register} />
          <SelectInput label="Courier" registerName="courier" register={register} watch={watch} setValue={setValue} options={[
            { name: "None"       , id: ""            },
            { name: "FedEx"      , id: "FedEx"       },
            { name: "Proovia"    , id: "Proovia"     },
            { name: "Parcelforce", id: "Parcelforce" },
            { name: "Royal Mail" , id: "RoyalMail"   },
            { name: "Other"      , id: "Other"       },
          ]} />
          <div className="col-span-2">
            <CheckboxInput
              label="Exclude Northern Ireland"
              registerName="excludeNorthernIreland"
              register={register}
              disabled={isEbayPolicy}
              cols=""
            />
            <p className="mt-1 text-xs text-gray-500">
              {isEbayPolicy
                ? "Set from this policy's excluded locations on eBay. Change it there and it will sync across."
                : "Buyers with a BT postcode won't be able to order parts on this policy from PartPro."}
            </p>
          </div>

          <ButtonInput isSubmit={true} classes="w-full" onClick={() => { }} label={policy ? "Update" : "Add"} />
          {policy && (
            <ButtonInput isSubmit={false} classes="w-full bg-red-600 hover:bg-red-700" onClick={() => handleDelete(policy.id)} label={"Delete"} />
          )}
        </form>
      </div>
    </Modal>
  );
}
